<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Button from 'dashboard/components-next/button/Button.vue';
import MiraSupportAPI from 'dashboard/api/miraSupport';
import SquaremeTour from './squareme/SquaremeTour.vue';

// Mira's help for the agent on this conversation. Everything Mira writes is
// labelled as AI and stays here: a reply reaches the customer only when an
// agent approves it (as written or edited), and is then sent as that agent's
// ordinary reply. Mira learns only knowledge a person has approved.
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const POLL_MS = 20000;
const TOUR_SETTING = 'mira_assist_tour_seen';
const TOUR_STEPS = [
  'label',
  'audience',
  'summary',
  'draft',
  'answer',
  'teach',
].map(target => ({
  target: `mira-${target}`,
  title: `CONVERSATION.MIRA_ASSIST.TOUR.${target.toUpperCase()}.TITLE`,
  body: `CONVERSATION.MIRA_ASSIST.TOUR.${target.toUpperCase()}.BODY`,
}));
// Warnings Mira returns with a draft that the agent should know about.
const WARNINGS = [
  'generated_amount_removed',
  'account_detail_removed',
  'unverified_fact_removed',
  'facts_unavailable',
  'facts_not_connected',
];
const ERRORS = [
  'budget_exhausted',
  'daily_limit_reached',
  'removed_detail',
  'not_pending',
  'context_too_large',
];

const audience = ref('');
const drafts = ref([]);
const edits = ref({});
const summary = ref(null);
const answer = ref(null);
const question = ref('');
const busy = ref('');
const tourRef = ref(null);
let timer = null;

const pending = computed(() =>
  drafts.value.filter(d => d.status === 'pending')
);
const history = computed(() =>
  drafts.value.filter(d => d.status !== 'pending').slice(0, 5)
);

const errorMessage = error => {
  const code = error?.response?.data?.error;
  return ERRORS.includes(code)
    ? t(`CONVERSATION.MIRA_ASSIST.ERRORS.${code.toUpperCase()}`)
    : t('CONVERSATION.MIRA_ASSIST.ERRORS.UNAVAILABLE');
};

const load = async () => {
  try {
    const { data } = await MiraSupportAPI.show(props.conversationId);
    audience.value = data.audience;
    drafts.value = data.drafts || [];
    pending.value.forEach(draft => {
      if (edits.value[draft.id] === undefined) {
        edits.value[draft.id] = draft.content;
      }
    });
  } catch {
    // The panel stays usable; the next poll tries again.
  }
};

const run = async (name, action) => {
  if (busy.value) return;
  busy.value = name;
  try {
    await action();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    busy.value = '';
  }
};

const summarise = () =>
  run('summary', async () => {
    const { data } = await MiraSupportAPI.summary(props.conversationId);
    summary.value = data;
  });

const suggest = () =>
  run('suggest', async () => {
    await MiraSupportAPI.suggestReply(props.conversationId);
    await load();
  });

const ask = () =>
  run('answer', async () => {
    const text = question.value.trim();
    if (!text) return;
    const { data } = await MiraSupportAPI.answer(props.conversationId, text);
    answer.value = data;
  });

const approve = draft =>
  run(`approve-${draft.id}`, async () => {
    await MiraSupportAPI.approveDraft(
      props.conversationId,
      draft.id,
      edits.value[draft.id] ?? draft.content
    );
    useAlert(t('CONVERSATION.MIRA_ASSIST.DRAFT.SENT'));
    await load();
  });

const discard = draft =>
  run(`discard-${draft.id}`, async () => {
    await MiraSupportAPI.discardDraft(props.conversationId, draft.id);
    await load();
  });

const teach = () =>
  run('teach', async () => {
    await MiraSupportAPI.proposeKnowledge(props.conversationId);
    useAlert(t('CONVERSATION.MIRA_ASSIST.TEACH.SENT'));
  });

const hasRemovedDetail = draft =>
  /\[(amount|account detail) removed/.test(edits.value[draft.id] ?? '');

const finishTour = () => updateUISettings({ [TOUR_SETTING]: true });

onMounted(async () => {
  await load();
  timer = setInterval(load, POLL_MS);
  if (!uiSettings.value?.[TOUR_SETTING]) {
    await nextTick();
    tourRef.value?.start();
  }
});

onBeforeUnmount(() => clearInterval(timer));

defineExpose({ load });
</script>

<template>
  <section
    class="flex flex-col gap-4 border-b border-n-weak p-4"
    data-test-id="mira-assist"
    :aria-label="$t('CONVERSATION.MIRA_ASSIST.HEADING')"
  >
    <div class="flex items-start justify-between gap-2">
      <div data-squareme-tour="mira-label">
        <h2 class="mb-0 text-base font-semibold text-n-slate-12">
          {{ $t('CONVERSATION.MIRA_ASSIST.HEADING') }}
        </h2>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('CONVERSATION.MIRA_ASSIST.INTRO') }}
        </p>
      </div>
      <Button
        size="xs"
        variant="ghost"
        color="slate"
        icon="i-lucide-circle-help"
        :title="$t('CONVERSATION.MIRA_ASSIST.TOUR.START')"
        :aria-label="$t('CONVERSATION.MIRA_ASSIST.TOUR.START')"
        @click="tourRef?.start()"
      />
    </div>

    <p
      v-if="audience"
      data-squareme-tour="mira-audience"
      data-test-id="mira-audience"
      class="mb-0 rounded-lg px-3 py-2 text-xs"
      :class="
        audience === 'verified_customer'
          ? 'bg-n-teal-3 text-n-teal-11'
          : 'bg-n-amber-3 text-n-amber-11'
      "
    >
      {{ $t(`CONVERSATION.MIRA_ASSIST.AUDIENCE.${audience.toUpperCase()}`) }}
    </p>

    <div data-squareme-tour="mira-summary" class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-text"
          data-test-id="mira-summarise"
          :label="$t('CONVERSATION.MIRA_ASSIST.SUMMARY.BUTTON')"
          :is-loading="busy === 'summary'"
          @click="summarise"
        />
        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-pen-line"
          data-test-id="mira-suggest"
          :label="$t('CONVERSATION.MIRA_ASSIST.DRAFT.BUTTON')"
          :is-loading="busy === 'suggest'"
          @click="suggest"
        />
      </div>
      <div
        v-if="summary"
        data-test-id="mira-summary"
        class="rounded-lg bg-n-surface-2 px-3 py-2 text-sm text-n-slate-12"
      >
        <span
          class="mb-1 inline-block rounded bg-n-iris-3 px-1.5 text-xs text-n-iris-11"
        >
          {{ $t('CONVERSATION.MIRA_ASSIST.LABEL.ai_summary') }}
        </span>
        <p class="mb-0 whitespace-pre-wrap">{{ summary.text }}</p>
      </div>
    </div>

    <div data-squareme-tour="mira-draft" class="flex flex-col gap-3">
      <p v-if="!pending.length" class="mb-0 text-xs text-n-slate-11">
        {{ $t('CONVERSATION.MIRA_ASSIST.DRAFT.NONE') }}
      </p>
      <article
        v-for="draft in pending"
        :key="draft.id"
        data-test-id="mira-draft"
        class="flex flex-col gap-2 rounded-lg border border-n-iris-6 bg-n-surface-1 p-3"
      >
        <div class="flex flex-wrap items-center gap-2 text-xs">
          <span class="rounded bg-n-iris-3 px-1.5 text-n-iris-11">
            {{ $t('CONVERSATION.MIRA_ASSIST.LABEL.ai_draft') }}
          </span>
          <span class="text-n-slate-11">
            {{
              draft.origin === 'customer_message'
                ? $t('CONVERSATION.MIRA_ASSIST.DRAFT.FROM_MESSAGE')
                : $t('CONVERSATION.MIRA_ASSIST.DRAFT.REQUESTED_BY', {
                    name: draft.requested_by,
                  })
            }}
          </span>
        </div>
        <label :for="`mira-draft-${draft.id}`" class="sr-only">
          {{ $t('CONVERSATION.MIRA_ASSIST.DRAFT.EDIT_LABEL') }}
        </label>
        <textarea
          :id="`mira-draft-${draft.id}`"
          v-model="edits[draft.id]"
          rows="4"
          class="w-full rounded-lg border border-n-weak bg-n-surface-1 p-2 text-sm text-n-slate-12"
        />
        <p
          v-for="warning in draft.warnings.filter(w => WARNINGS.includes(w))"
          :key="warning"
          class="mb-0 text-xs text-n-amber-11"
        >
          {{ $t(`CONVERSATION.MIRA_ASSIST.WARNINGS.${warning}`) }}
        </p>
        <p v-if="draft.citations?.length" class="mb-0 text-xs text-n-slate-11">
          {{
            $t('CONVERSATION.MIRA_ASSIST.SOURCES', {
              sources: draft.citations.map(c => c.title).join(', '),
            })
          }}
        </p>
        <p
          v-if="hasRemovedDetail(draft)"
          data-test-id="mira-removed-detail"
          class="mb-0 text-xs text-n-ruby-11"
        >
          {{ $t('CONVERSATION.MIRA_ASSIST.DRAFT.REMOVED_DETAIL') }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            color="slate"
            :label="$t('CONVERSATION.MIRA_ASSIST.DRAFT.DISCARD')"
            :is-loading="busy === `discard-${draft.id}`"
            @click="discard(draft)"
          />
          <Button
            size="sm"
            data-test-id="mira-approve"
            :label="$t('CONVERSATION.MIRA_ASSIST.DRAFT.APPROVE')"
            :disabled="hasRemovedDetail(draft) || !edits[draft.id]?.trim()"
            :is-loading="busy === `approve-${draft.id}`"
            @click="approve(draft)"
          />
        </div>
      </article>
      <ul
        v-if="history.length"
        class="mb-0 flex flex-col gap-1 text-xs text-n-slate-11"
      >
        <li v-for="draft in history" :key="draft.id">
          {{
            $t(
              `CONVERSATION.MIRA_ASSIST.HISTORY.${draft.status.toUpperCase()}`,
              {
                name: draft.decided_by || '',
              }
            )
          }}
        </li>
      </ul>
    </div>

    <form
      data-squareme-tour="mira-answer"
      class="flex flex-col gap-2"
      @submit.prevent="ask"
    >
      <label for="mira-question" class="text-sm font-medium text-n-slate-12">
        {{ $t('CONVERSATION.MIRA_ASSIST.ANSWER.LABEL') }}
      </label>
      <div class="flex gap-2">
        <input
          id="mira-question"
          v-model="question"
          maxlength="2000"
          class="flex-1 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-1.5 text-sm text-n-slate-12"
          :placeholder="$t('CONVERSATION.MIRA_ASSIST.ANSWER.PLACEHOLDER')"
        />
        <Button
          size="sm"
          type="submit"
          variant="faded"
          :label="$t('CONVERSATION.MIRA_ASSIST.ANSWER.BUTTON')"
          :disabled="!question.trim()"
          :is-loading="busy === 'answer'"
        />
      </div>
      <div
        v-if="answer"
        data-test-id="mira-answer"
        class="rounded-lg bg-n-surface-2 px-3 py-2 text-sm text-n-slate-12"
      >
        <span
          class="mb-1 inline-block rounded bg-n-iris-3 px-1.5 text-xs text-n-iris-11"
        >
          {{ $t('CONVERSATION.MIRA_ASSIST.LABEL.ai_answer') }}
        </span>
        <p class="mb-0 whitespace-pre-wrap">{{ answer.text }}</p>
        <p
          v-if="answer.citations?.length"
          class="mb-0 mt-1 text-xs text-n-slate-11"
        >
          {{
            $t('CONVERSATION.MIRA_ASSIST.SOURCES', {
              sources: answer.citations.map(c => c.title).join(', '),
            })
          }}
        </p>
      </div>
    </form>

    <div data-squareme-tour="mira-teach" class="flex items-center gap-2">
      <Button
        size="xs"
        variant="ghost"
        icon="i-lucide-graduation-cap"
        data-test-id="mira-teach"
        :label="$t('CONVERSATION.MIRA_ASSIST.TEACH.BUTTON')"
        :is-loading="busy === 'teach'"
        @click="teach"
      />
      <span class="text-xs text-n-slate-11">
        {{ $t('CONVERSATION.MIRA_ASSIST.TEACH.HINT') }}
      </span>
    </div>

    <SquaremeTour ref="tourRef" :steps="TOUR_STEPS" @finish="finishTour" />
  </section>
</template>
